/**
 * BatchCard Component
 * Mobile card display for batches to pay
 * Used by BatchesToPayTab
 * Wrapped with React.memo for performance optimization
 */

import { memo } from 'react';
import PlanDueHint from './PlanDueHint';
import { DocumentTextIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency, getBatchId, getBatchTypeBadge, isLinkableBatch, describeBatch, clampCurrency, formatAmountOnBlur, nonNegativeAmount } from '../utils/formatters';

const BatchCard = memo(({
  batch,
  selected,
  onSelect,
  paymentAmounts = {},
  onAmountChange,
  getBatchInquiryUrl,
  disabled = false,
  // Payment-plan batches can expand a detail panel (rendered by the parent below this
  // card); when onToggleExpand is provided the header shows the chevron for it. Called
  // with the batch so the parent can pass one stable callback for every card.
  expanded = false,
  onToggleExpand,
  // Payment-plan collection batch: what is due this month (null otherwise). The BUF
  // amount defaults to it; the hint makes clear the rest is the plan's full balance.
  planAmountDue = null
}) => {
  const batchId = getBatchId(batch);
  // API returns totalNet/totalBuf/totalMisc. Negative buckets (credits) stay visible
  // in the per-bucket NET/BUF/MISC sections below, but the headline "Total Owed"
  // reflects what the user can actually pay — credits don't reduce other buckets.
  const netDue = batch.totalNet || batch.openNet || 0;
  const bufDue = batch.totalBuf || batch.openBuf || 0;
  const miscDue = batch.totalMisc || batch.openMisc || 0;
  const totalOwed = nonNegativeAmount(netDue) + nonNegativeAmount(bufDue) + nonNegativeAmount(miscDue);
  const typeBadge = getBatchTypeBadge(batch);

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(batch, !selected);
  };

  const handleAmountChange = (field, value) => {
    // Allow empty string for better UX
    if (value === '') {
      onAmountChange(batchId, field, '');
      return;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      onAmountChange(batchId, field, '');
      return;
    }
    // Map field names for max value lookup
    const maxValueMap = {
      'openNet': netDue,
      'openBuf': bufDue,
      'openMisc': miscDue
    };
    const maxValue = maxValueMap[field] || 0;
    // Clamp value between 0 and max, with proper rounding
    onAmountChange(batchId, field, clampCurrency(numValue, maxValue));
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border shadow-md p-4 mb-3 transition-colors ${
        selected
          ? 'border-primary dark:border-primary-light bg-primary-50/30 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Header Row */}
      <div className="flex items-center gap-3 mb-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleCheckboxChange}
          disabled={disabled}
          className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <DocumentTextIcon className="h-4 w-4 text-gray-400" />
            {!isLinkableBatch(batch) || !getBatchInquiryUrl ? (
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Batch #{batch.reportNumber || batchId}
              </h3>
            ) : (
              <a
                href={getBatchInquiryUrl(batch)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-semibold text-primary dark:text-primary-light hover:underline cursor-pointer"
              >
                Batch #{batch.reportNumber || batchId}
              </a>
            )}
            {typeBadge && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                {typeBadge.label}
              </span>
            )}
            {onToggleExpand && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleExpand(batch); }}
                aria-label={expanded ? 'Hide plan details' : 'Show plan details'}
                aria-expanded={expanded}
                className="flex items-center gap-0.5 text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200"
              >
                {expanded
                  ? <ChevronDownIcon className="h-4 w-4" />
                  : <ChevronRightIcon className="h-4 w-4" />}
                Plan
              </button>
            )}
          </div>
          {describeBatch(batch) !== '-' && (
            <p className="text-xs text-gray-600 dark:text-gray-300 ml-6 truncate font-medium">
              {describeBatch(batch)}
            </p>
          )}
          {batch.agencyName && (
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6 truncate">
              {batch.agencyName}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Owed</p>
          <p className="text-sm font-bold text-primary dark:text-primary-light">
            ${formatCurrency(totalOwed)}
          </p>
        </div>
      </div>

      {/* Amount Grid */}
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
        {/* NET */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">NET Due</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ${formatCurrency(netDue)}
          </p>
          {selected && netDue > 0 && (
            <input
              type="number"
              value={paymentAmounts[`${batchId}-openNet`] ?? formatAmountOnBlur(netDue)}
              onChange={(e) => handleAmountChange('openNet', e.target.value)}
              onBlur={(e) => onAmountChange(batchId, 'openNet', formatAmountOnBlur(e.target.value))}
              className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0"
              max={netDue}
              step="0.01"
            />
          )}
        </div>

        {/* BUF */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">BUF Due</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ${formatCurrency(bufDue)}
          </p>
          {selected && bufDue > 0 && (
            <input
              type="number"
              value={paymentAmounts[`${batchId}-openBuf`] ?? formatAmountOnBlur(bufDue)}
              onChange={(e) => handleAmountChange('openBuf', e.target.value)}
              onBlur={(e) => onAmountChange(batchId, 'openBuf', formatAmountOnBlur(e.target.value))}
              className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0"
              max={bufDue}
              step="0.01"
            />
          )}
          <PlanDueHint amountDue={planAmountDue} balance={bufDue} />
        </div>

        {/* MISC */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">MISC Due</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ${formatCurrency(miscDue)}
          </p>
          {selected && miscDue > 0 && (
            <input
              type="number"
              value={paymentAmounts[`${batchId}-openMisc`] ?? formatAmountOnBlur(miscDue)}
              onChange={(e) => handleAmountChange('openMisc', e.target.value)}
              onBlur={(e) => onAmountChange(batchId, 'openMisc', formatAmountOnBlur(e.target.value))}
              className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0"
              max={miscDue}
              step="0.01"
            />
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render when these specific props change
  const prevBatchId = getBatchId(prevProps.batch);
  const nextBatchId = getBatchId(nextProps.batch);

  // Check if batch identity changed
  if (prevBatchId !== nextBatchId) return false;

  // Check if selection state changed
  if (prevProps.selected !== nextProps.selected) return false;

  // Check if disabled state changed
  if (prevProps.disabled !== nextProps.disabled) return false;

  // Check if the plan-detail expander changed (open/closed or availability)
  if (prevProps.expanded !== nextProps.expanded) return false;
  if (prevProps.planAmountDue !== nextProps.planAmountDue) return false;
  if (prevProps.onToggleExpand !== nextProps.onToggleExpand) return false;

  // Check if getBatchInquiryUrl callback changed
  if (prevProps.getBatchInquiryUrl !== nextProps.getBatchInquiryUrl) return false;

  // Check if payment amounts for THIS batch changed
  const prevNet = prevProps.paymentAmounts[`${prevBatchId}-openNet`];
  const nextNet = nextProps.paymentAmounts[`${nextBatchId}-openNet`];
  const prevBuf = prevProps.paymentAmounts[`${prevBatchId}-openBuf`];
  const nextBuf = nextProps.paymentAmounts[`${nextBatchId}-openBuf`];
  const prevMisc = prevProps.paymentAmounts[`${prevBatchId}-openMisc`];
  const nextMisc = nextProps.paymentAmounts[`${nextBatchId}-openMisc`];

  if (prevNet !== nextNet || prevBuf !== nextBuf || prevMisc !== nextMisc) return false;

  // Props are equal, don't re-render
  return true;
});

export default BatchCard;
